import React, { useState, useEffect } from 'react';

import { useResize } from 'hooks/use-resize';

interface IProps {
  children: React.ReactNode;
}

export interface IUiContext {
  isMobile: boolean | null;
  isDesktop: boolean | null;
  isIE11: boolean;
  navOpen: boolean;
  toggleNav: (open: boolean) => void;
  searchOpen: boolean;
  toggleSearch: (searchOpen: boolean) => void;
  // for homepage animation
  homepageLoaded: boolean;
  setHomepageLoaded: (loaded: boolean) => void;
  homepageAnimationComplete: boolean;
  setHomepageAnimationComplete: (homepageAnimationComplete: boolean) => void;
  isHomepage: boolean;
  setHomepage: (isHomepage: boolean) => void;
  canTransition: boolean;
  setCanTransition: (canTransition: boolean) => void;
}

export const UIContext = React.createContext<IUiContext>({
  isMobile: null,
  isDesktop: null,
  isIE11: false,
  navOpen: false,
  toggleNav: (open: boolean) => !open,
  searchOpen: false,
  toggleSearch: (searchOpen: boolean) => !searchOpen,
  homepageLoaded: false,
  setHomepageLoaded: (homepageLoaded: boolean) => !homepageLoaded,
  isHomepage: true,
  setHomepage: (isHomepage: boolean) => !isHomepage,
  canTransition: true,
  setCanTransition: (canTransition: boolean) => !canTransition,
  homepageAnimationComplete: false,
  setHomepageAnimationComplete: (homepageAnimationComplete: boolean) => !homepageAnimationComplete,
});

export const UIProvider = ({ children }: IProps) => {
  const [isIE11, setIsIE11] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [homepageLoaded, setHomepageLoaded] = React.useState(false);
  const [isHomepage, setHomepage] = React.useState(true); // currently homepage only, used post-intro animation
  const [canTransition, setCanTransition] = React.useState(true); // page should transition
  const [homepageAnimationComplete, setHomepageAnimationComplete] = useState(false);

  const preventScroll = (prevent: boolean) => {
    const htmlClassName = 'scroll-disabled';

    prevent
      ? document.documentElement.classList.add(htmlClassName)
      : document.documentElement.classList.remove(htmlClassName);
  };

  const toggleNav = (open: boolean) => {
    preventScroll(open);
    setNavOpen(open);
    if (open && searchOpen) {
      setSearchOpen(false);
    }
  };

  const toggleSearch = (open: boolean) => {
    preventScroll(open);
    setSearchOpen(open);

    if (open && navOpen) {
      setNavOpen(false);
    }
  };

  const resize = useResize();
  const { isMobile, isDesktop } = resize;

  useEffect(() => {
    if (window.document.documentMode) {
      setIsIE11(true);
    };

  }, []);

  return (
    <UIContext.Provider
      value={{
        isMobile,
        isDesktop,
        isIE11,
        navOpen,
        toggleNav,
        searchOpen,
        toggleSearch,
        homepageLoaded,
        setHomepageLoaded,
        isHomepage,
        setHomepage,
        canTransition,
        setCanTransition,
        homepageAnimationComplete,
        setHomepageAnimationComplete,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const withUIContext = (Component: any) => (props: any) => (
  <UIContext.Consumer>{context => <Component {...props} ui={context} />}</UIContext.Consumer>
);
